import { useEffect, useCallback, useMemo, useState } from "react";
import { toast } from "@components/toast/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "@components";
import {
  GroupNameStep,
  AccountSelectionStep,
  EditMenuStep,
  DeleteGroupStep,
} from "./components";
import {
  useGroupModal,
  PAGES,
  useGroupForm,
  useAccounts,
  useGroupSave,
  useGroupDelete,
} from "./hooks";
import { useAccountSelection } from "@hooks";
import { useCustomerGroups } from "@context/CustomerGroupsContext";

export function CreateEditGroupModal({ isOpen, onClose, editingGroup = null }) {
  const navigate = useNavigate();
  const { groupId: currentRouteGroupId } = useParams();

  // Form state
  const { control, errors, handleSubmit, getName, setName, resetForm } =
    useGroupForm();

  // Account selection state
  const {
    selectedAccounts,
    allSelected,
    deselectedIds,
    isRowSelected,
    toggleRowSelection,
    selectAll,
    deselectAll,
    selectAccounts,
    deselectAccounts,
    initializeSelection,
    resetSelection,
  } = useAccountSelection();

  // Accounts data
  const {
    loading,
    searchTerm,
    setSearchTerm,
    filteredAccounts,
    totalRows,
    searchResultCount,
    hasMoreData,
    fetchAccounts,
    fetchMore,
    fetchGroupMembers,
    fetchAllSearchResults,
    setPriorityAccounts,
    resetSearch,
  } = useAccounts();

  // Tracks whether a "Select All" fetch is in progress
  const [selectingAll, setSelectingAll] = useState(false);

  // Save logic
  const { saving, saveGroup } = useGroupSave();

  // Delete logic
  const { deleting, deleteGroup } = useGroupDelete();

  // Groups context for refreshing after changes
  const { refreshGroups } = useCustomerGroups();

  // Reset all state when modal closes
  const handleReset = useCallback(() => {
    resetForm();
    resetSelection();
    resetSearch();
  }, [resetForm, resetSelection, resetSearch]);

  // Modal page state
  const {
    page,
    isEditMode,
    goToName,
    goToAccounts,
    goToEditMenu,
    goToDelete,
    getModalTitle,
  } = useGroupModal({
    isOpen,
    editingGroup,
    onReset: handleReset,
  });

  // Load existing group data when editing
  useEffect(() => {
    if (isOpen && editingGroup) {
      setName(editingGroup.name);
      loadGroupData();
    }
  }, [isOpen, editingGroup]);

  const loadGroupData = async () => {
    const members = await fetchGroupMembers(editingGroup?.id);
    initializeSelection(members);
    setPriorityAccounts(members);
    fetchAccounts();
  };

  const buildSavePayload = useCallback(
    () => ({
      name: getName(),
      selectedAccounts,
      selectAll: allSelected,
      excludeCustomerIds: [...deselectedIds],
      editingGroupId: editingGroup?.id,
    }),
    [getName, selectedAccounts, allSelected, deselectedIds, editingGroup],
  );

  // Create flow: name step → accounts step
  const handleNameSubmitCreate = () => {
    handleSubmit(() => {
      goToAccounts();
      fetchAccounts();
    })();
  };

  // Edit flow: name step → save and return to menu
  const handleNameSubmitEdit = () => {
    handleSubmit(async () => {
      const savePayload = buildSavePayload();
      const success = await saveGroup(savePayload);
      if (success) {
        await refreshGroups();
        onClose();
        toast({ description: `Renamed Group to "${savePayload.name}"` });
      }
    })();
  };

  const handleAccountsSave = async () => {
    const savePayload = buildSavePayload()
    const groupId = await saveGroup(savePayload);
    if (groupId) {
      await refreshGroups();
      handleReset();
      onClose();
      toast({ description: isEditMode ? `Updated "${savePayload.name}" Group` : `Created "${savePayload.name}" Group` });
      // Navigate to the newly created group
      if (!isEditMode) {
        navigate(`/accounts/groups/${groupId}`);
      }
    }
  };

  const handleDelete = async () => {
    const groupName = editingGroup?.name;
    const success = await deleteGroup(editingGroup?.id);
    if (success) {
      await refreshGroups();
      if (currentRouteGroupId === editingGroup?.id) {
        navigate("/accounts");
      }
      onClose();
      toast({ description: `Deleted "${groupName}" Group` });
    }
  };

  // Reflect the true count across all pages
  const selectedCount = allSelected
    ? totalRows - deselectedIds.size
    : selectedAccounts.length;

  // When search is active, select/deselect ALL accounts matching the search
  // (not just the ones loaded so far). When no search, use the global flag.
  const handleSelectAll = async () => {
    if (searchTerm.trim()) {
      setSelectingAll(true);
      try {
        const allResults = await fetchAllSearchResults();
        if (allResults) selectAccounts(allResults);
      } finally {
        setSelectingAll(false);
      }
    } else {
      selectAll();
    }
  };

  const handleDeselectAll = () => {
    deselectAll();
  };

  const renderPage = () => {
    switch (page) {
      case PAGES.EDIT_MENU:
        return (
          <EditMenuStep
            onChangeName={goToName}
            onEditAccounts={goToAccounts}
            onDelete={goToDelete}
          />
        );

      case PAGES.NAME:
        return (
          <GroupNameStep
            control={control}
            errors={errors}
            onSubmit={
              isEditMode ? handleNameSubmitEdit : handleNameSubmitCreate
            }
            onCancel={isEditMode ? goToEditMenu : onClose}
            submitLabel={isEditMode ? "Save" : "Continue"}
            cancelLabel={isEditMode ? "Back" : "Cancel"}
          />
        );

      case PAGES.ACCOUNTS:
        return (
          <AccountSelectionStep
            loading={loading}
            saving={saving}
            selectingAll={selectingAll}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            accounts={filteredAccounts}
            selectedCount={selectedCount}
            isEditMode={isEditMode}
            isRowSelected={isRowSelected}
            onToggleRow={toggleRowSelection}
            onBack={isEditMode ? goToEditMenu : goToName}
            onSave={handleAccountsSave}
            onLoadMore={fetchMore}
            hasMoreData={hasMoreData}
            totalRows={totalRows}
            searchResultCount={searchResultCount}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
          />
        );

      case PAGES.DELETE:
        return (
          <DeleteGroupStep
            groupName={getName()}
            onConfirm={handleDelete}
            onCancel={goToEditMenu}
            deleting={deleting}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle(getName())}
      description={
        page === PAGES.ACCOUNTS ? "Choose the accounts in this group" : null
      }
      size="sm"
    >
      {renderPage()}
    </Modal>
  );
}
